import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/lib/AdminAuthContext";
import { formatApiError } from "@/lib/adminApi";

export default function AdminLogin() {
  const { admin, loading, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a2421] text-[#f9f6f0] grid place-items-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-full border border-[#bc6c25] grid place-items-center font-serif italic text-lg text-[#bc6c25]">E</div>
            <p className="font-serif text-2xl">Eliora <em className="text-[#bc6c25]">CRM</em></p>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#f9f6f0]/60">Panel privado de Sarai</p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-[#f9f6f0]/5 backdrop-blur-xl border border-[#f9f6f0]/10 rounded-3xl p-8 space-y-5"
        >
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#f9f6f0]/60">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="mt-2 bg-[#f9f6f0]/5 border-[#f9f6f0]/15 text-[#f9f6f0] rounded-2xl py-6 placeholder:text-[#f9f6f0]/40"
              placeholder="sssarai@hotmail.com"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#f9f6f0]/60">Contraseña</Label>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="mt-2 bg-[#f9f6f0]/5 border-[#f9f6f0]/15 text-[#f9f6f0] rounded-2xl py-6"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#ff8a65] bg-[#ff8a65]/10 border border-[#ff8a65]/20 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white py-6 text-sm font-semibold tracking-wide disabled:opacity-50"
          >
            <Lock className="w-4 h-4 mr-2" />
            {submitting ? "Entrando…" : "Entrar"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>

        <p className="mt-6 text-center text-[11px] text-[#f9f6f0]/40">
          Solo Sarai puede acceder. Si olvidaste tu contraseña, contacta al equipo técnico para rotarla.
        </p>
      </div>
    </div>
  );
}
